import {
    Button,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Skeleton,
} from '@longy/web-components'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { CalendarOff, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { BookingNotice } from '../../components/booking/BookingNotice'
import { BookingStep } from '../../components/booking/BookingStep'
import { SlotGroups } from '../../components/booking/SlotGroups'
import { api } from '../../core/trpc'
import { useLocale } from '../../hooks/useLocale'

const PATH = '/booking/video-consultation'

/**
 * Only entries that lead to a calendar with something in it. Opt-in on the API: the app lists
 * services whose calendar is empty today on purpose, and this must not change that.
 *
 * Which services belong on the web is decided per service by `isWebVisible`, which every one of
 * these procedures already applies from the request — there is nothing to ask for here.
 */
const BOOKABLE_CATALOG = { withBookableSlotsOnly: true } as const

/** Radix refuses an empty item value, so "no category" needs a name of its own. */
const ALL_CATEGORIES = 'all'

const videoSearchSchema = z.object({
    /** Set when arriving from the catalogue, which has already picked the expert. */
    expertId: z.string().optional(),
    categoryId: z.string().optional(),
})

const VideoConsultationBookingPage = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { getLocalizedValue } = useLocale()
    const { expertId: prefilledExpertId, categoryId: prefilledCategoryId } = Route.useSearch()

    const [expertId, setExpertId] = useState<string | undefined>(prefilledExpertId)
    const [categoryId, setCategoryId] = useState<string | undefined>(prefilledCategoryId)
    const [slotId, setSlotId] = useState<string | undefined>()

    /**
     * Every expert with a free slot, not only those in the chosen category: the category narrows
     * the calendars below, not who can be picked, which is the order the app's booking screen uses.
     */
    const {
        data: experts,
        isPending: isExpertsPending,
        isError: isExpertsError,
        refetch: refetchExperts,
    } = api.videoSlot.getExpertsWithSlots.useQuery({
        serviceType: 'VIDEO_CONSULTATION',
        ...BOOKABLE_CATALOG,
    })

    /** The category assigned to the service, not the service type. */
    const { data: categories } = api.purchasableCategory.getAllActiveForUser.useQuery({
        purchasableTypes: ['EXPERT_SERVICE', 'EXPERT_SERVICE_SLOT'],
        serviceType: 'VIDEO_CONSULTATION',
        ...BOOKABLE_CATALOG,
    })

    const { data: slotGroups, isPending: isSlotsPending } =
        api.videoSlot.getGroupedSlotsForUser.useQuery(
            {
                expertId: expertId ?? '',
                // Left out entirely for "all categories", which is what makes every one of the
                // expert's services come back as its own calendar.
                categoryId,
                serviceType: 'VIDEO_CONSULTATION' as const,
            },
            { enabled: !!expertId },
        )

    const selectedSlot = (slotGroups ?? [])
        .flatMap((group) => group.slots)
        .find((slot) => slot.id === slotId)
    const purchasableItemId = selectedSlot?.purchasableItem?.id

    /*
     * A failed query settles the same way an empty one does — not pending, no data — so the error
     * has to be answered first or a visitor is told nobody is available when the request simply
     * never arrived.
     */
    if (isExpertsError) {
        return (
            <BookingStep
                title={t('frontend.booking.videoConsultation')}
                step={t('frontend.booking.title')}
            >
                <BookingNotice
                    icon={TriangleAlert}
                    title={t('frontend.booking.loadFailedTitle')}
                    description={t('frontend.booking.loadFailedLead')}
                    action={{
                        label: t('frontend.booking.tryAgain'),
                        onClick: () => void refetchExperts(),
                    }}
                />
            </BookingStep>
        )
    }

    /*
     * `getExpertsWithSlots` is already filtered to experts who have something bookable, so an empty
     * answer means nobody has a free slot — not that the clinic has no experts. Without this the
     * page offered an empty picker under "choose an expert" and then asked the visitor to choose
     * from options that were not there.
     */
    if (!isExpertsPending && !experts?.length) {
        return (
            <BookingStep
                title={t('frontend.booking.videoConsultation')}
                step={t('frontend.booking.title')}
            >
                <BookingNotice
                    icon={CalendarOff}
                    title={t('frontend.booking.noBookableTitle')}
                    description={t('frontend.booking.noBookableVideoLead')}
                />
            </BookingStep>
        )
    }

    return (
        <BookingStep
            title={t('frontend.booking.videoConsultation')}
            step={t('frontend.booking.title')}
        >
            <div className="flex flex-col gap-6">
                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="expert">{t('frontend.booking.chooseExpert')}</Label>
                        {isExpertsPending ? (
                            <Skeleton className="h-10 w-full" />
                        ) : (
                            <Select
                                value={expertId}
                                onValueChange={(value) => {
                                    setExpertId(value)
                                    setSlotId(undefined)
                                }}
                            >
                                <SelectTrigger id="expert">
                                    <SelectValue placeholder={t('frontend.booking.chooseExpert')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {experts?.map((expert) => (
                                        <SelectItem key={expert.id} value={expert.id}>
                                            {expert.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>

                    {!!categories?.length && (
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="category">{t('app.booking.selectCategory')}</Label>
                            <Select
                                value={categoryId ?? ALL_CATEGORIES}
                                onValueChange={(value) => {
                                    setCategoryId(value === ALL_CATEGORIES ? undefined : value)
                                    setSlotId(undefined)
                                }}
                            >
                                <SelectTrigger id="category">
                                    <SelectValue placeholder={t('app.booking.allCategories')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ALL_CATEGORIES}>
                                        {t('app.booking.allCategories')}
                                    </SelectItem>
                                    {categories.map((category) => (
                                        <SelectItem key={category.id} value={category.id}>
                                            {getLocalizedValue(category.name)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>

                {!expertId ? (
                    <p className="rounded-lg bg-slate-50 p-4 text-sm text-brand-gray">
                        {t('common.booking.chooseFromTheOptionsToShowTheDates')}
                    </p>
                ) : isSlotsPending ? (
                    <Skeleton className="h-96 w-full" />
                ) : !slotGroups?.length ? (
                    <p className="text-brand-gray">{t('frontend.booking.noSlotsAvailable')}</p>
                ) : (
                    <>
                        <SlotGroups
                            groups={slotGroups}
                            selectedSlotId={slotId}
                            isCategorySelected={!!categoryId}
                            onSelect={setSlotId}
                        />
                        <Button
                            className="self-end"
                            disabled={!purchasableItemId}
                            onClick={() =>
                                purchasableItemId &&
                                navigate({
                                    to: '/booking/checkout',
                                    search: { purchasableItemId },
                                })
                            }
                        >
                            {t('frontend.booking.continueToPayment')}
                        </Button>
                    </>
                )}
            </div>
        </BookingStep>
    )
}

export const Route = createFileRoute(PATH)({
    validateSearch: videoSearchSchema,
    head: () => ({ meta: [{ title: 'Long-Y - Videokonzultáció foglalása' }] }),
    component: VideoConsultationBookingPage,
})
